import { useState, useEffect } from "react";
import { Card, Button, Form, ProgressBar } from "react-bootstrap";
import { MdRefresh } from "react-icons/md";
import { FaCopy, FaCheck } from "react-icons/fa";

/* ---------- PIN GENERATION ---------- */
const generatePIN = (length, excludeDuplicates) => {
  const digits = "0123456789";
  let pin = "";

  if (excludeDuplicates && length > digits.length) {
    length = digits.length;
  }

  let availableDigits = digits.split("");

  for (let i = 0; i < length; i++) {
    const index = Math.floor(Math.random() * availableDigits.length);
    pin += availableDigits[index];

    if (excludeDuplicates) {
      availableDigits.splice(index, 1);
    }
  }

  return pin;
};

/* ---------- PIN STRENGTH ---------- */
const calculateStrength = (length) => {
  let strength = 0;
  let color = "danger";

  if (length >= 4) strength = 30;
  if (length >= 6) strength = 60;
  if (length >= 8) strength = 80;
  if (length >= 10) strength = 100;

  if (strength >= 80) color = "success";
  else if (strength >= 60) color = "warning";
  else if (strength >= 30) color = "info";

  return { strength, color };
};

const PinGenerator = ({isDark}) => {
  const [pin, setPin] = useState("");
  const [copied, setCopied] = useState(false);
  const [length, setLength] = useState(6);
  const [excludeDuplicates, setExcludeDuplicates] = useState(false);

  useEffect(() => {
    setPin(generatePIN(length, excludeDuplicates));
  }, [length, excludeDuplicates]);

  const regeneratePin = () => {
    setPin(generatePIN(length, excludeDuplicates));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(pin);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const { strength, color } = calculateStrength(length);

  return (
    <Card className={`pin-card shadow-sm border ${!isDark && "bg-light"}`}>
      <h5 className="text-center fw-bold mb-3">PIN Generator</h5>

      <div className="pin-display mb-3">
        {pin.split("").map((digit, index) => (
          <div key={index} className="pin-box">
            {digit}
          </div>
        ))}
      </div>

      <div className="mb-3">
        <small className="fw-semibold">Strength</small>
        <ProgressBar
          now={strength}
          variant={color}
          style={{ height: 6 }}
        />
      </div>

      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">PIN Length</Form.Label>
        <div className="d-flex align-items-center gap-2">
          <Form.Range
            min={4}
            max={12}
            value={length}
            onChange={(e) => setLength(Number(e.target.value))}
          />
          <Form.Control
            type="number"
            className="interactive-input rounded-1"
            value={length}
            onChange={(e) => setLength(Number(e.target.value))}
            style={{
              width: 65,
              textAlign: "center",
              fontSize: 12,
              padding: "6px 6px"
            }}
          />
        </div>
      </Form.Group>

      <Form.Check
        type="switch"
        label="Exclude duplicate digits"
        checked={excludeDuplicates}
        onChange={() => setExcludeDuplicates(!excludeDuplicates)}
        className="mb-3"
      />

      <div className="d-flex gap-2">
        <Button
          variant="outline-secondary rounded-1 hstack gap-2 justify-content-center"
          className="w-100"
          onClick={regeneratePin}
        >
          <MdRefresh size={18} /> Generate
        </Button>

        <Button
          variant={copied ? "success" : "primary"}
          className="w-100 rounded-1 hstack gap-2 justify-content-center"
          onClick={copyToClipboard}
        >
          {copied ? <FaCheck size={15} /> : <FaCopy size={15} />}{" "}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </Card>
  );
};

export default PinGenerator;
